import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { HTTPError, post } from "common/helpers/HTTP";
import type { ConversationParticipant } from "common/types";

import useUpdateConversation from "./useUpdateConversation";

import type { ConversationMessage, UploadedFile } from "../types";

async function sendMessageRequest(
  workspaceId: string,
  accountId: string,
  message: string,
  recipients: ConversationParticipant[],
  uploadedFiles?: UploadedFile[],
) {
  const {
    conversation_urn: conversationUrn,
    message_urn: messageUrn,
    created_at: createdAt,
  } = await post(
    `workspaces/${workspaceId}/accounts/${accountId}/conversations`,
    {
      message: message.trim(),
      recipients: recipients.map(({ id }) => id),
      file_identifiers: uploadedFiles?.map((file) => file.id),
    },
  );

  return {
    message,
    createdAt,
    recipients,
    conversationUrn,
    messageUrn,
  };
}
async function markAsReadRequest(
  workspaceId: string,
  accountId: string,
  conversationUrn: string,
) {
  await post(
    `workspaces/${workspaceId}/accounts/${accountId}/conversations/${conversationUrn}/read`,
  );
}
async function markAsUnreadRequest(
  workspaceId: string,
  accountId: string,
  conversationUrn: string,
): Promise<number> {
  const { unread_count } = await post(
    `workspaces/${workspaceId}/accounts/${accountId}/conversations/${conversationUrn}/unread`,
  );
  return unread_count;
}

export default function useConversationUpdates() {
  const queryClient = useQueryClient();
  const { id: workspaceId, accounts } = useSelectedWorkspaceContext();
  const { updateConversation, addMessage } = useUpdateConversation();
  const { leadsKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: sendMessage, isPending: isSendingMessage } = useMutation(
    {
      mutationFn: ({
        accountId,
        message,
        recipients,
        uploadedFiles,
      }: {
        accountId: string;
        message: string;
        recipients: ConversationParticipant[];
        uploadedFiles?: UploadedFile[];
      }) =>
        sendMessageRequest(
          workspaceId,
          accountId,
          message,
          recipients,
          uploadedFiles,
        ),
      onSuccess: (
        {
          message,
          createdAt,
          recipients,
          conversationUrn: returnedConversationUrn,
          messageUrn,
        },
        { accountId, uploadedFiles },
      ) => {
        const senderAccount = accounts?.find((acc) => acc.id === accountId);

        const newMessage: ConversationMessage = {
          urn: messageUrn,
          body: message,
          created_at: createdAt,
          sender: {
            id: accountId,
            full_name: senderAccount?.full_name,
            picture_url: senderAccount?.picture_url,
          },
          inmail: false,
          attachments: uploadedFiles?.map(
            ({ file_input, file_url, id, name }) => ({
              byte_size: file_input.mimetype.includes("image/")
                ? null
                : file_input.size,
              identifier: id,
              media_type: file_input.mimetype.includes("image/")
                ? null
                : file_input.mimetype,
              name: file_input.mimetype.includes("image/") ? null : name,
              url: file_url,
            }),
          ),
        };

        updateConversation(
          returnedConversationUrn,
          (draftConversation) => {
            draftConversation.last_message = newMessage;
            // this is for conversations that are new in the cache
            if (!draftConversation.participants) {
              draftConversation.participants = recipients;
            }
            if (!draftConversation.last_activity_at) {
              draftConversation.last_activity_at = createdAt;
            }
          },
          accountId,
        );
        addMessage(returnedConversationUrn, newMessage);

        // for conversations with leads that don't have a conversationUrn yet
        if (
          recipients.length === 1 &&
          !queryClient.getQueryData(leadsKeys.conversationUrn(recipients[0].id))
        ) {
          queryClient.setQueryData(
            leadsKeys.conversationUrn(recipients[0].id),
            returnedConversationUrn,
          );
        }
      },
      onError: (error) => {
        if (error instanceof HTTPError) {
          const errorMessages: Record<string, string> = {
            RecipientNotConnection:
              "You can only send messages to your first degree connections",
            BlockedByRecipient:
              "Cannot send message because you have been blocked by the recipient",
          };

          const message = errorMessages[error.data];
          if (message) {
            toast.error(message);
          } else {
            toast.error("Unable to send a message, please try again later");
          }
        }
      },
    },
  );

  const { mutateAsync: markAsRead } = useMutation({
    mutationFn: ({
      accountId,
      conversationUrn,
    }: {
      accountId: string;
      conversationUrn: string;
    }) => markAsReadRequest(workspaceId, accountId, conversationUrn),
    onSuccess: (_, { conversationUrn }) => {
      updateConversation(conversationUrn, (draftConversation) => {
        draftConversation.unread_count = 0;
      });
    },
  });

  const { mutateAsync: markAsUnread } = useMutation({
    mutationFn: ({
      accountId,
      conversationUrn,
    }: {
      accountId: string;
      conversationUrn: string;
    }) => markAsUnreadRequest(workspaceId, accountId, conversationUrn),
    onSuccess: (unreadCount, { conversationUrn }) => {
      updateConversation(conversationUrn, (draftConversation) => {
        draftConversation.unread_count = unreadCount;
      });
    },
    onError: () => {
      toast.error("Unable to mark as unread, please try again later");
    },
  });

  return { sendMessage, isSendingMessage, markAsRead, markAsUnread };
}
